// src/features/medicine-inventory/components/stock/MultiMedicinePurchaseForm.jsx
import React, { useState } from 'react';
import { FaPlus, FaTrash, FaShoppingCart, FaFileInvoice } from 'react-icons/fa';
import { useInventory } from '../../context/InventoryContext';
import { generatePurchaseOrderNumber } from '../../utils/helpers';

const MultiMedicinePurchaseForm = ({ onClose }) => {
  const { medicines, addStockMovement, categories } = useInventory();
  const [formData, setFormData] = useState({
    supplierName: '',
    supplierContact: '',
    purchaseOrderNumber: generatePurchaseOrderNumber(),
    invoiceNumber: '',
    purchaseDate: new Date().toISOString().split('T')[0],
    expectedDeliveryDate: '',
    paymentTerms: 'NET_30',
    paymentStatus: 'PENDING',
    notes: '',
    medicines: [{
      medicineId: '',
      quantity: 1,
      unitPrice: '',
      batchNumber: '',
      expiryDate: '',
      discount: 0,
      taxRate: 0
    }]
  });
  const [submitting, setSubmitting] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  const handleAddMedicine = () => {
    setFormData(prev => ({
      ...prev,
      medicines: [...prev.medicines, {
        medicineId: '',
        quantity: 1,
        unitPrice: '',
        batchNumber: '',
        expiryDate: '',
        discount: 0,
        taxRate: 0
      }]
    }));
  };

  const handleRemoveMedicine = (index) => {
    setFormData(prev => ({
      ...prev,
      medicines: prev.medicines.filter((_, i) => i !== index)
    }));
  };

  const handleMedicineChange = (index, field, value) => {
    const updatedMedicines = [...formData.medicines];
    
    if (field === 'medicineId') {
      const selectedMedicine = medicines.find(m => m.id === value);
      updatedMedicines[index] = {
        ...updatedMedicines[index],
        medicineId: value,
        unitPrice: selectedMedicine?.price || '',
        medicineName: selectedMedicine?.name || '',
        manufacturer: selectedMedicine?.manufacturer || '',
        category: selectedMedicine?.category || ''
      };
    } else {
      updatedMedicines[index][field] = value;
    }
    
    setFormData(prev => ({
      ...prev,
      medicines: updatedMedicines
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    
    try {
      // Validate all required fields
      for (const item of formData.medicines) {
        if (!item.medicineId) {
          alert('Please select a medicine for all entries');
          setSubmitting(false);
          return;
        }
        if (!item.quantity || item.quantity <= 0) {
          alert('Please enter valid quantity for all medicines');
          setSubmitting(false);
          return;
        }
        if (!item.unitPrice || item.unitPrice <= 0) {
          alert('Please enter valid unit price for all medicines');
          setSubmitting(false);
          return;
        }
      }

      // Calculate totals for each medicine
      const medicinesWithTotals = formData.medicines.map(item => {
        const subtotal = item.quantity * item.unitPrice;
        const discountAmount = subtotal * (item.discount / 100);
        const taxableAmount = subtotal - discountAmount;
        const taxAmount = taxableAmount * (item.taxRate / 100);
        const total = taxableAmount + taxAmount;

        return {
          ...item,
          subtotal,
          discountAmount,
          taxableAmount,
          taxAmount,
          total
        };
      });

      // Calculate overall totals
      const subtotalTotal = medicinesWithTotals.reduce((sum, item) => sum + item.subtotal, 0);
      const discountTotal = medicinesWithTotals.reduce((sum, item) => sum + item.discountAmount, 0);
      const taxTotal = medicinesWithTotals.reduce((sum, item) => sum + item.taxAmount, 0);
      const grandTotal = medicinesWithTotals.reduce((sum, item) => sum + item.total, 0);

      // Create multi-medicine purchase
      await addStockMovement({
        type: 'MULTI_PURCHASE',
        ...formData,
        medicines: medicinesWithTotals,
        summary: {
          subtotal: subtotalTotal,
          discount: discountTotal,
          tax: taxTotal,
          total: grandTotal
        }
      });
      
      onClose();
    } catch (error) {
      console.error('Failed to create purchase order:', error);
      alert('Failed to create purchase order. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const calculateItemTotal = (item) => {
    const subtotal = (parseInt(item.quantity || 0) * parseFloat(item.unitPrice || 0));
    const discountAmount = subtotal * (parseFloat(item.discount || 0) / 100);
    const taxableAmount = subtotal - discountAmount;
    const taxAmount = taxableAmount * (parseFloat(item.taxRate || 0) / 100);
    return taxableAmount + taxAmount;
  };

  const calculateGrandTotal = () => {
    return formData.medicines.reduce((sum, item) => {
      return sum + calculateItemTotal(item);
    }, 0);
  };

  const getFilteredMedicines = () => {
    if (!searchTerm) return medicines;
    return medicines.filter(med => 
      med.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      med.manufacturer?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      med.category?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6 max-h-[90vh] overflow-y-auto px-2">
      {/* Purchase Order Header */}
      <div className="bg-gradient-to-r from-green-50 to-blue-50 p-4 rounded-lg">
        <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
          <FaShoppingCart className="mr-2 text-green-600" />
          Purchase Order Details
        </h3>
        
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Supplier Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
              value={formData.supplierName}
              onChange={(e) => setFormData({ ...formData, supplierName: e.target.value })}
              placeholder="Enter supplier name"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Supplier Contact
            </label>
            <input
              type="text"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
              value={formData.supplierContact}
              onChange={(e) => setFormData({ ...formData, supplierContact: e.target.value })}
              placeholder="Phone or email"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Purchase Order Number
            </label>
            <input
              type="text"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 bg-gray-100"
              value={formData.purchaseOrderNumber}
              readOnly
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Invoice Number
            </label>
            <input
              type="text"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
              value={formData.invoiceNumber}
              onChange={(e) => setFormData({ ...formData, invoiceNumber: e.target.value })}
              placeholder="Supplier invoice number"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Purchase Date <span className="text-red-500">*</span>
            </label>
            <input
              type="date"
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
              value={formData.purchaseDate}
              onChange={(e) => setFormData({ ...formData, purchaseDate: e.target.value })}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Expected Delivery Date
            </label>
            <input
              type="date"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
              value={formData.expectedDeliveryDate}
              onChange={(e) => setFormData({ ...formData, expectedDeliveryDate: e.target.value })}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Payment Terms
            </label>
            <select
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
              value={formData.paymentTerms}
              onChange={(e) => setFormData({ ...formData, paymentTerms: e.target.value })}
            >
              <option value="NET_30">Net 30 Days</option>
              <option value="NET_15">Net 15 Days</option>
              <option value="NET_7">Net 7 Days</option>
              <option value="CASH">Cash on Delivery</option>
              <option value="PREPAID">Prepaid</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Payment Status
            </label>
            <select
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
              value={formData.paymentStatus}
              onChange={(e) => setFormData({ ...formData, paymentStatus: e.target.value })}
            >
              <option value="PENDING">Pending</option>
              <option value="PAID">Paid</option>
              <option value="PARTIAL">Partial Payment</option>
            </select>
          </div>
        </div>

        <div className="mt-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Notes
          </label>
          <textarea
            rows="2"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
            value={formData.notes}
            onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
            placeholder="Additional notes about this purchase order"
          />
        </div>
      </div>

      {/* Medicines List */}
      <div className="bg-white rounded-lg border border-gray-200">
        <div className="px-4 py-3 border-b border-gray-200 flex justify-between items-center">
          <h3 className="text-lg font-medium text-gray-900">Medicines & Quantities</h3>
          <button
            type="button"
            onClick={handleAddMedicine}
            className="inline-flex items-center px-3 py-1 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700"
          >
            <FaPlus className="mr-1" /> Add Medicine
          </button>
        </div>

        {/* Medicine Search/Filter */}
        <div className="p-4 border-b border-gray-200">
          <input
            type="text"
            placeholder="Search medicines by name, manufacturer, or category..."
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        <div className="p-4 space-y-4 max-h-[400px] overflow-y-auto">
          {formData.medicines.map((item, index) => {
            const medicine = medicines.find(m => m.id === item.medicineId);
            const filteredMedicines = getFilteredMedicines();
            const itemTotal = calculateItemTotal(item);
            
            return (
              <div key={index} className="flex items-start space-x-3 p-4 bg-gray-50 rounded-lg border border-gray-200">
                <div className="flex-1 space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        Medicine <span className="text-red-500">*</span>
                      </label>
                      <select
                        required
                        className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                        value={item.medicineId}
                        onChange={(e) => handleMedicineChange(index, 'medicineId', e.target.value)}
                      >
                        <option value="">Select Medicine</option>
                        {filteredMedicines.map(med => (
                          <option key={med.id} value={med.id}>
                            {med.name} - {med.manufacturer} ({med.category.name})
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        Quantity <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="number"
                        required
                        min="1"
                        className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                        value={item.quantity}
                        onChange={(e) => handleMedicineChange(index, 'quantity', parseInt(e.target.value))}
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        Unit Price ($) <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="number"
                        required
                        min="0"
                        step="0.01"
                        className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                        value={item.unitPrice}
                        onChange={(e) => handleMedicineChange(index, 'unitPrice', parseFloat(e.target.value))}
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        Batch Number
                      </label>
                      <input
                        type="text"
                        className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                        value={item.batchNumber}
                        onChange={(e) => handleMedicineChange(index, 'batchNumber', e.target.value)}
                        placeholder="Optional"
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        Expiry Date
                      </label>
                      <input
                        type="date"
                        className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                        value={item.expiryDate}
                        onChange={(e) => handleMedicineChange(index, 'expiryDate', e.target.value)}
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        Discount (%)
                      </label>
                      <input
                        type="number"
                        min="0"
                        max="100"
                        step="0.01"
                        className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                        value={item.discount}
                        onChange={(e) => handleMedicineChange(index, 'discount', parseFloat(e.target.value))}
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        Tax Rate (%)
                      </label>
                      <input
                        type="number"
                        min="0"
                        max="100"
                        step="0.01"
                        className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                        value={item.taxRate}
                        onChange={(e) => handleMedicineChange(index, 'taxRate', parseFloat(e.target.value))}
                      />
                    </div>

                    <div className="flex items-center justify-end">
                      <div className="text-right">
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          Item Total
                        </label>
                        <p className="text-sm font-bold text-green-600">
                          ${itemTotal.toFixed(2)}
                        </p>
                      </div>
                    </div>
                  </div>

                  {medicine && (
                    <div className="text-xs text-gray-500 mt-1">
                      Current Stock: {medicine.currentStock} {medicine.unit}s | 
                      Manufacturer: {medicine.manufacturer} | 
                      Category: {medicine.category}
                    </div>
                  )}
                </div>

                {formData.medicines.length > 1 && (
                  <button
                    type="button"
                    onClick={() => handleRemoveMedicine(index)}
                    className="mt-1 text-red-600 hover:text-red-800"
                  >
                    <FaTrash />
                  </button>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Summary Section */}
      <div className="bg-gradient-to-r from-green-50 to-blue-50 p-4 rounded-lg">
        <h3 className="text-md font-medium text-gray-900 mb-3 flex items-center">
          <FaFileInvoice className="mr-2 text-green-600" />
          Purchase Summary
        </h3>
        
        <div className="space-y-2">
          <div className="flex justify-between items-center">
            <span className="text-gray-600">Subtotal:</span>
            <span className="font-medium">
              ${formData.medicines.reduce((sum, item) => 
                sum + (parseInt(item.quantity || 0) * parseFloat(item.unitPrice || 0)), 0
              ).toFixed(2)}
            </span>
          </div>
          
          <div className="flex justify-between items-center">
            <span className="text-gray-600">Total Discount:</span>
            <span className="font-medium text-red-600">
              -${formData.medicines.reduce((sum, item) => {
                const subtotal = (parseInt(item.quantity || 0) * parseFloat(item.unitPrice || 0));
                const discount = subtotal * (parseFloat(item.discount || 0) / 100);
                return sum + discount;
              }, 0).toFixed(2)}
            </span>
          </div>
          
          <div className="flex justify-between items-center">
            <span className="text-gray-600">Total Tax:</span>
            <span className="font-medium">
              +${formData.medicines.reduce((sum, item) => {
                const subtotal = (parseInt(item.quantity || 0) * parseFloat(item.unitPrice || 0));
                const discountAmount = subtotal * (parseFloat(item.discount || 0) / 100);
                const taxableAmount = subtotal - discountAmount;
                const taxAmount = taxableAmount * (parseFloat(item.taxRate || 0) / 100);
                return sum + taxAmount;
              }, 0).toFixed(2)}
            </span>
          </div>
          
          <div className="border-t border-gray-300 pt-2 mt-2">
            <div className="flex justify-between items-center">
              <span className="text-lg font-bold text-gray-900">Grand Total:</span>
              <span className="text-2xl font-bold text-green-600">
                ${calculateGrandTotal().toFixed(2)}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Form Actions */}
      <div className="flex justify-end space-x-3 pt-4 sticky bottom-0 bg-white py-4 border-t">
        <button
          type="button"
          onClick={onClose}
          className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
          disabled={submitting}
        >
          Cancel
        </button>
        <button
          type="submit"
          className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
          disabled={submitting || formData.medicines.length === 0}
        >
          <FaShoppingCart className="mr-2" />
          {submitting ? 'Processing...' : 'Create Purchase Order'}
        </button>
      </div>
    </form>
  );
};

export default MultiMedicinePurchaseForm;